import React from 'react';
import { Zap, Crown, Clock, ArrowRight, ShieldCheck } from 'lucide-react';
import { UserProfile } from '../types';
import { HideOnPlayStore } from './HideOnPlayStore';

interface TrialBannerProps {
  user: UserProfile | null;
  onStartTrial?: () => void;
  onOpenPaywall?: () => void;
  isActivatingTrial?: boolean;
}

export default function TrialBanner({
  user,
  onStartTrial,
  onOpenPaywall,
  isActivatingTrial = false
}: TrialBannerProps) {
  if (!user || user.isMasterUser) return null;

  // 1. Caso: Trial Especialista Ativo
  if (user.isTrialActive && user.trialEndsAt) {
    const remainingDays = Math.max(
      1,
      Math.ceil((new Date(user.trialEndsAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    );

    return (
      <div className="w-full bg-linear-to-r from-orange-600 via-amber-600 to-amber-500 text-white px-4 py-2.5 shadow-md flex items-center justify-between gap-3 text-xs sm:text-sm animate-in fade-in duration-300">
        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
          <div className="p-1 sm:p-1.5 bg-white/20 rounded-xl shrink-0">
            <Crown className="h-4 w-4 sm:h-5 sm:w-5 text-amber-200 animate-bounce" />
          </div>
          <p className="font-semibold truncate">
            <span className="font-black uppercase tracking-wider text-amber-200 mr-1.5">
              Trial Especialista Ativo:
            </span>
            <span>Acesso completo liberado! Restam </span>
            <span className="font-black underline decoration-amber-300 decoration-2 underline-offset-2">
              {remainingDays} {remainingDays === 1 ? 'dia' : 'dias'}
            </span>.
          </p>
        </div>

        {onOpenPaywall && (
          <HideOnPlayStore>
            <button
              type="button"
              onClick={onOpenPaywall}
              className="shrink-0 bg-white text-orange-700 hover:bg-orange-50 font-black text-xs px-3.5 py-1.5 rounded-xl transition-all shadow-sm hover:scale-105 active:scale-95 flex items-center gap-1.5 cursor-pointer"
            >
              <span>Garantir Plano</span>
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </HideOnPlayStore>
        )}
      </div>
    );
  }

  // 2. Caso: Trial Expirou e Usuário está no Grátis (dados 100% preservados)
  if (!user.isTrialActive && user.trialUsed && user.plan === 'Grátis') {
    return (
      <div className="w-full bg-zinc-900 border-b border-zinc-800 text-zinc-300 px-4 py-2 shadow-sm flex items-center justify-between gap-3 text-xs">
        <div className="flex items-center gap-2 min-w-0">
          <Clock className="h-4 w-4 text-amber-400 shrink-0" />
          <p className="truncate text-zinc-300">
            <span className="font-bold text-white">Seu período de teste de 14 dias encerrou.</span> Seus orçamentos e clientes estão <span className="text-emerald-400 font-semibold">100% salvos e seguros</span>.
          </p>
        </div>

        {onOpenPaywall && (
          <HideOnPlayStore>
            <button
              type="button"
              onClick={onOpenPaywall}
              className="shrink-0 bg-brand hover:bg-brand-dark text-white font-bold text-xs px-3 py-1 rounded-lg transition-all flex items-center gap-1 cursor-pointer"
            >
              <span>Ver Planos</span>
              <ArrowRight className="h-3 w-3" />
            </button>
          </HideOnPlayStore>
        )}
      </div>
    );
  }

  // 3. Caso: Usuário Grátis que ainda NÃO usou o Trial (Incentivo de Conversão)
  if (user.plan === 'Grátis' && user.trialUsed === false && onStartTrial) {
    return (
      <div className="w-full bg-linear-to-r from-emerald-950 via-zinc-900 to-zinc-900 border-b border-emerald-500/30 text-emerald-100 px-4 py-2 shadow-sm flex items-center justify-between gap-3 text-xs">
        <div className="flex items-center gap-2 min-w-0">
          <Zap className="h-4 w-4 text-emerald-400 shrink-0" />
          <p className="truncate">
            <span className="font-bold text-white">Experimente 14 dias grátis</span> de todos os recursos PRO e Especialista, sem cartão!
          </p>
        </div>

        <button
          type="button"
          disabled={isActivatingTrial}
          onClick={onStartTrial}
          className="shrink-0 bg-emerald-500 hover:bg-emerald-400 text-neutral-950 font-black text-xs px-3 py-1 rounded-lg transition-all flex items-center gap-1 shadow-sm cursor-pointer disabled:opacity-50"
        >
          <span>{isActivatingTrial ? 'Ativando...' : 'Ativar 14 Dias Grátis'}</span>
          <ArrowRight className="h-3 w-3" />
        </button>
      </div>
    );
  }

  return null;
}
